import {useAddBanner, useFocusBanner} from '@github-ui/banner-stack'
import {useMutation, useQueryClient, type QueryKey} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'

interface WorkflowRunActionResponse {
  message: string
}

interface WorkflowRunActionOptions {
  onDeleteError?: (error: Error) => boolean
  onDeleteSuccess?: () => void
}

async function runWorkflowRunAction(path: string, method: 'PUT' | 'DELETE'): Promise<WorkflowRunActionResponse> {
  const response = await reactFetchJSON(path, {method})
  let body: unknown

  try {
    body = await response.json()
  } catch {
    body = undefined
  }

  const message =
    typeof body === 'object' && body !== null && 'message' in body && typeof body.message === 'string'
      ? body.message
      : undefined

  if (!response.ok) {
    throw new Error(message ?? 'The workflow run action could not be completed.')
  }
  if (!message) {
    throw new Error('The workflow run action returned an invalid response.')
  }

  return {message}
}

export function useWorkflowRunActions(
  queryKey: QueryKey,
  {onDeleteError, onDeleteSuccess}: WorkflowRunActionOptions = {},
) {
  const queryClient = useQueryClient()
  const addBanner = useAddBanner()
  const focusBanner = useFocusBanner()

  const showBanner = (title: string, variant: 'success' | 'critical') => {
    const id = addBanner({title, variant, dismissible: true})
    requestAnimationFrame(() => focusBanner(id))
  }

  const cancelMutation = useMutation<WorkflowRunActionResponse, Error, string>({
    mutationFn: path => runWorkflowRunAction(path, 'PUT'),
    onSuccess: async data => {
      showBanner(data.message, 'success')
      await queryClient.invalidateQueries({queryKey, exact: true})
    },
    onError: error => showBanner(error.message, 'critical'),
  })

  const deleteMutation = useMutation<WorkflowRunActionResponse, Error, string>({
    mutationFn: path => runWorkflowRunAction(path, 'DELETE'),
    onSuccess: async data => {
      onDeleteSuccess?.()
      showBanner(data.message, 'success')
      await queryClient.invalidateQueries({queryKey, exact: true})
    },
    onError: error => {
      if (!onDeleteError?.(error)) showBanner(error.message, 'critical')
    },
  })

  return {cancelMutation, deleteMutation}
}
